import crews from "csi/access/crews";
import config from "csi/config";
import configPreferences from "csi/config/preferences";
import Action from "csi/def/Action";
import html from "csi/html";
import locale from "csi/locale";
import { ATTR_APPLICABLE_FOR_TYPE, ATTR_PROPERTIES_CUSTOM_VIEW, TYPE_SUSTAINABILITY_PROFILE_REVISION } from "csi/model/sustainabilityProfile";
import PropertyWidget from "csi/property/Widget";
import { REVISION_DRAFT } from "csi/reflection/reflectionConstants";
import sessionConstants from "csi/sessionConstants";
import { routerStore } from "csi/state/rootStore";
import PluginFactory from "csi/view/PluginFactory";
import ToolbarPluginButton from "csi/view/ToolbarPluginButton";
import xml from "csi/xml";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";
import { CUSTOM_VIEW_VERSION } from "src/c8-platform/type/customView";

export const SP_CV_MANAGE = "SPCustomViewManage";
const SUSTAINABILITY_ACCESS_ROLES = ["Sustainability Administrator", 
	"Sustainability Editor",
	"TDS - Sustainability Profile Administrator",
	"TDS - Sustainability Profile Creator",
	"TDS - Sustainability Profile Editor"];
const CV_ACCESS_ROLES = ["Custom View Administrator", 
	"Custom View Editor"];

PluginFactory.declare(SP_CV_MANAGE, ToolbarPluginButton, {
	materialIcon: "add",
	constructor() {
		this.defAction = lang.mixin(new Action(), {
			classController: "csi/property/PropertyCustomViewController",
			m_class: html.CSS_BUTTON_PRIMARY,
			m_name: locale.resource("SelectSomething", [locale.resource("Attributes")]),
			m_id: SP_CV_MANAGE,
		});
		this.defAction.resolve();
	},

	/**
	 * Only show action if Template (no parent) is in Draft state
	 */
	canShowPlugin() {
		const cache = this.getView().getCache();
		const susProfileRevision = this.getView().getViewNode();
		const susProfile = cache.getNode(susProfileRevision?.__Parent__);
		return susProfile && xml.isEmptyURL(susProfile.__Parent__) && susProfileRevision?.State === REVISION_DRAFT;
	},

	/**
	 * Allow users with Sustainability Admin or Editor, 
	 * Sustainability Profile Admin, Creator, or Editor, AND 
	 * Custom View Admin or Editor to change custom view
	 * @override
	 */
	validatePlugin(ctx) {
		return sessionConstants.admin || (crews.isInRoles(SUSTAINABILITY_ACCESS_ROLES, ctx.getContextNode()) && crews.isInRoles(CV_ACCESS_ROLES, ctx.getContextNode()));
	},
});

const SustainabilityProfileProperties = declare(PropertyWidget, {
	/** @property {string} applicableForType - SustainabilityProfileSubtypeSettings' applicableForType which is used for path hints */
	applicableForType: "",
	manageSingleCustomView: true,

	/**
	 * @override
	 * Add primary button action
	 */
	onViewCreate() {
		this.plugins = [SP_CV_MANAGE];
	},

	/**
	 * @override
	 * Make custom view container specific to subtype
	 */
	_resolveView() {
		let spDataSheet = this.getCache().get(this.getViewNode()?.__Parent__);
		// when creating React view, spDataSheet may not be in the cache yet, so use router store to get subtype info for setting pathhints 
		spDataSheet ??= routerStore.locationNode;
		if (spDataSheet) {
			// if SP datasheet is in the cache, this means we are viewing a custom view, so update applicableForType and pathHints
			this.applicableForType = xml.enumGetText(config.getSubtypeSetting(spDataSheet.Subtype, ATTR_APPLICABLE_FOR_TYPE) as string);
			this.customViewPathHints = TYPE_SUSTAINABILITY_PROFILE_REVISION + "." + this.applicableForType;
			this.defView.pathHintsContext = configPreferences.getContextualPathHints(TYPE_SUSTAINABILITY_PROFILE_REVISION + "." + this.applicableForType);
			if (routerStore.locationRevisionNode?.State === "Revision.State:APPROVED") {
				this.defView.bReadOnly = true;
			}
		}
		this.inherited(arguments);
	},

	/**
	 * @override
	 * Use view preference key per Product Type for custom view container path hints per Product Type
	 */
	getViewPreferenceKey() {
		return this.inherited(arguments) + "/" + this.applicableForType;
	},

	/**
	 * @override
	 * Use SPRevision.PropertiesCustomView for urlDefaultView
	 */
	refreshCustomViewStore(bResetStore) {
		const propertiesCustomView = this.getViewNode()?.PropertiesCustomView;
		if (propertiesCustomView) {
			this.urlDefaultView = propertiesCustomView;
			this.customViewPathHints = TYPE_SUSTAINABILITY_PROFILE_REVISION + "." + this.applicableForType;
		}
		return this.inherited(arguments);
	},

	/**
	 * @override
	 * Attach Custom View to SPRevision
	 */
	onSaveCustomView(cn, options) {
		const { locationRevisionUrl } = routerStore;
		cn.attach(locationRevisionUrl, ATTR_PROPERTIES_CUSTOM_VIEW);

		// send request to update TDSCustomView with print options selected on the properties custom view
		cn.writer.callMethod("Specification", "CopyOverOptionsToTDSView")
			.addParameter("CNL", xml.ATTR_CNL, locationRevisionUrl)
			.addParameter("Options", xml.ATTR_STRING, JSON.stringify(options))
			.addParameter("VR", xml.ATTR_INTEGER, CUSTOM_VIEW_VERSION);
	},

});

export default SustainabilityProfileProperties;